// Ad swap's form (spec G14-ad-swap; the playbook is the spec and live has a card only, so no
// capture: AdSwap-1 and AdSwap-2): our staged form in one approval. The script stage picks the
// product (one of the brand's, or an uploaded packshot) and the source ad (a video of the library),
// takes an optional actor note and the member's word on the rights, reads the source and writes
// the swapped script part by part (the server's reading and swap director, `ad-swap-draft.ts`),
// and approves it; Cast shows the new actor's still with its ↻. "Generate video" starts the run
// (`templates/ad_swap.py`).
import { Button, Group, Loader, Stack, Switch, Text, Textarea, Tooltip } from "@mantine/core";
import { Clapperboard, Monitor, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { AdSwapDraft, AdSwapScript, Job } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest, type AdSwapInputsOutput } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { MediaPreview } from "../../../platform/media/MediaPreview";
import { useRefPicture } from "../../../platform/media/ref-picture";
import { Choice } from "../../../ui/Choice";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ReuseSwitch, VideoSlot, type SlotVideo, type TemplateFormProps } from "./kit";
import { InputChip, ScriptSection } from "./PodcastScript";
import { StillCard } from "./StillCard";
import { Submit } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { useAdSwapDraft, videoBody, type DraftWork } from "./ad-swap-draft";
import classes from "./podcast.module.css";

/** Our words: the server's for the same checks (`source_ad.py`, `swap_draft.py`). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "Your product and the exact photo of it the new ad shows. Pick one of your products and its " +
    "packshot, or upload a packshot — it replaces the pack in the source ad.",
  name: "The product name the new line says. Leave empty and it is read off the packshot.",
  text:
    "A description or the product page. Every claim in the new line is quoted from it — leave " +
    "empty and the words printed on the pack are used.",
};
const SOURCE_TIP =
  "The ad to remake: a video of your library, 4 to 30 seconds long. Its cuts, framing and pacing " +
  "are kept; the actor, the product and the line are swapped.";
const RIGHTS_LABEL = "I have the rights to remake this ad";
const ACTOR_TIP =
  "Who replaces the person in the source ad: age, hair, outfit. Leave empty and the director " +
  "writes a new original person.";
const SCRIPT_TIP =
  "One line a part of the source ad, each said over its cuts, at about the source's pace. Edit any " +
  "line before approving — every product fact is quoted from the product text.";
const RESOLUTION_TIP = "Resolution of every clip. 480p costs less than half of 720p.";
const FORMAT_TIP = "Every clip is vertical 9:16.";
const WRITE_SCRIPT = "Write the lines yourself";
const SCRIPT_HINT = { wand: "to read the source ad and write the lines, or", pencil: "to write them yourself." };
const APPROVE_SCRIPT = "Approve script";
const GENERATE_VIDEO = "Generate video";
const HANDOFF_NOTICE = "Your ad swap is finishing in My Generations — you can close this panel now.";
const RESOLUTIONS: readonly AdSwapInputsOutput["resolution"][] = ["720p", "480p", "1080p"];
const PICK_PRODUCT = "Pick a product and its packshot";
const PICK_SOURCE = "Pick a source ad from your library";
const CONFIRM_RIGHTS = "Confirm you have the rights to remake this ad";
const GENERATE_FIRST = "Generate the script first: it reads the source ad's parts";
const ACTOR_PENDING = "Wait for the actor's still";
const SOURCE_LENGTH = "Pick a source ad of 4 to 30 seconds";
const SOURCE_TOO_SMALL = "Pick a source ad at least 300 pixels on its short side";
const UNREADABLE_PACK =
  "The pack was hard to read — type the product name or its text and generate again.";
/** Approve script's needs, the server's words (`swap_draft.approval_problems`). */
const NEEDS = { line: "Every part needs its line", actor: "Describe the new actor", pack: "Describe the new product's pack", source: "Describe the source ad's pack" };
/** The script's caps on its texts (`AdSwapScript`). */
const MAX_LOOK = { actor_description: 600, product_description: 1_200, source_product: 200 } as const;
const WORDS_PER_SECOND = 3.5;
const WORD_SLACK = 1.15;
const LETTERS = "abcdefgh";

/** A picked video's problem, as the server words it at Generate Script. */
export function sourceProblem(video: { durationMs?: number | null; width?: number | null; height?: number | null }) {
  const ms = video.durationMs ?? null;
  if (ms !== null && (ms < 4_000 || ms > 30_000)) return SOURCE_LENGTH;
  const { width, height } = video;
  if (width && height && Math.min(width, height) < 300) return SOURCE_TOO_SMALL;
  return null;
}

/** What keeps the script from approval, in the server's words; null when nothing does. */
export function approvalIssue(script: AdSwapScript | null): string | null {
  if (script === null) return null;
  const problems = [
    ...(script.parts.some((part) => part.line.trim() === "") ? [NEEDS.line] : []),
    ...(script.actor_description.trim() === "" ? [NEEDS.actor] : []),
    ...(script.product_description.trim() === "" ? [NEEDS.pack] : []),
    ...(script.source_product.trim() === "" ? [NEEDS.source] : []),
  ];
  return problems.length === 0 ? null : problems.join("; ");
}

function seconds(value: number): string {
  return `${Math.round(value * 10) / 10}`;
}

/** Each part's most words: about 3.5 a second, with the server's slack (`swap_draft.word_cap`). */
function wordCaps(draft: AdSwapDraft): number[] {
  return (draft.reading?.parts ?? []).map((part) =>
    Math.ceil(WORDS_PER_SECOND * (part.end_seconds - part.start_seconds) * WORD_SLACK),
  );
}

/** The script with its spoken lines cleared for the member to write: every direction the
 * director wrote (the beats, the packs, the set, the person) kept, as the clips need them. */
export function blankLines(script: AdSwapScript): AdSwapScript {
  return { ...script, parts: script.parts.map((part) => ({ ...part, line: "" })) };
}

function progressOf(work: DraftWork): string | null {
  if (work.packing) return "Reading the pack";
  if (work.reading) return "Reading the source ad";
  if (work.writing) return "Writing the script";
  return work.drawing ? "Drawing the actor" : null;
}

/** The source ad's slot: the video picked here, else the draft's, read from the library. */
function useSourceVideo(teamId: string, brandId: string, assetId: string | null, picked: SlotVideo | null) {
  const ref = assetId === null ? null : { kind: "asset" as const, asset_id: assetId };
  const read = useRefPicture(teamId, brandId, picked === null ? ref : null);
  if (assetId === null) return null;
  if (picked !== null && "assetId" in picked && picked.assetId === assetId) return picked;
  return { assetId, url: read.still, name: read.name ?? "Source ad" };
}

export function AdSwapForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useAdSwapDraft(teamId, brandId);
  const [picked, setPicked] = useState<SlotVideo | null>(null);
  const [asked, setAsked] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [writeAsked, setWriteAsked] = useState(false);
  const { form, draft } = flow;
  const sourceId = form?.inputs.source_ad?.asset_id ?? null;
  const source = useSourceVideo(teamId, brandId, sourceId, picked);
  const approved = draft !== undefined && draft.stage !== "script";
  // After Approve script the resolution is the form's own, as Pixar Talking Ad's: the run's price
  // turns on it and the cast does not, so it stays editable until the run starts.
  const [chosen, setChosen] = useState<AdSwapInputsOutput["resolution"] | null>(null);
  if (!approved && chosen !== null) setChosen(null);
  const resolution = (approved ? chosen : null) ?? form?.inputs.resolution ?? "720p";
  const video = draft === undefined ? null : videoBody(draft, resolution);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: (job: Job) => {
      setStarted(true);
      onStarted(job);
    },
  });

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Ad swap</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null) return panel(<Loader size="sm" aria-label="Loading the draft" />);

  const { inputs, script } = form;
  const setInputs = (change: Partial<AdSwapInputsOutput>) => flow.editInputs(change);
  const editScript = (change: Partial<AdSwapScript>) => {
    if (script !== null) flow.edit({ script: { ...script, ...change } });
  };
  const locked = !flow.editable;
  const writing = flow.work.packing || flow.work.reading || flow.work.writing;
  const progress = progressOf(flow.work);
  const problem = picked === null ? null : sourceProblem(picked);
  const missing = [
    ...(inputs.product ? [] : [PICK_PRODUCT]),
    ...(inputs.source_ad ? [] : [PICK_SOURCE]),
    ...(inputs.rights ? [] : [CONFIRM_RIGHTS]),
  ];
  const issue = approvalIssue(script);
  const caps = wordCaps(draft);
  const actor = draft.stills.find((each) => each.name === "actor") ?? null;

  let submit;
  if (approved) {
    const ready = actor?.status === "done";
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [ACTOR_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || videoStart.start.isPending || started}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [...missing, ...(script === null ? [GENERATE_FIRST] : [])];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || problem !== null || flow.busy}
        loading={flow.approving}
        onClick={flow.approve}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TIP}
          width={88}
          value={resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={flow.busy || started}
          onChange={(next) => {
            const picked = RESOLUTIONS.find((each) => each === next);
            if (picked === undefined) return;
            if (approved) setChosen(picked);
            else setInputs({ resolution: picked });
          }}
        />
        <Tooltip label={FORMAT_TIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {started ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );

  if (approved) {
    const still = actor ?? { status: "pending" as const, url: null, problem: null };
    return panel(
      <div className={classes.content}>
        <Stack gap={12} aria-label="Cast" component="section">
          <Group justify="space-between" wrap="nowrap">
            <Text className={classes.castTitle}>Cast</Text>
            <Button
              variant="subtle"
              size="compact-sm"
              disabled={flow.resetting || flow.busy}
              onClick={() => setConfirming(true)}
            >
              Start over
            </Button>
          </Group>
          <div className={classes.stills}>
            <StillCard
              label="Actor"
              still={still}
              waiting={flow.work.drawing}
              busy={flow.busy || started}
              onRegenerate={flow.regenerate}
              onFixLabel={null}
              onPreview={() => setPreview("actor")}
            />
          </div>
          <MediaPreview
            items={
              actor?.url
                ? [{ id: "actor", name: "Actor", kind: "image", url: actor.url, thumb: actor.url, poster: null, durationMs: null, width: null, height: null }]
                : []
            }
            current={preview}
            onShow={setPreview}
          />
          <ConfirmModal
            opened={confirming}
            title="Start over"
            confirmLabel="Start over"
            pending={flow.resetting}
            onCancel={() => setConfirming(false)}
            onConfirm={() => {
              setStarted(false);
              flow.startOver();
              setConfirming(false);
            }}
          >
            <Text size="sm">The script, the source ad's reading and the actor are cleared, and the form starts empty.</Text>
          </ConfirmModal>
        </Stack>
        {status}
      </div>,
      footer,
    );
  }

  const warnings = [
    ...(draft.pack_warning ? [UNREADABLE_PACK] : []),
    ...(draft.script_problems.length > 0 && script !== null
      ? [`The script check failed twice: ${draft.script_problems.join("; ")}. Fix the lines before approving.`]
      : []),
  ];
  const parts = draft.reading?.parts ?? [];
  const rows =
    script === null
      ? null
      : script.parts.map((part, at) => {
          const planned = parts[at];
          const span = planned === undefined ? "" : ` · ${seconds(planned.start_seconds)}–${seconds(planned.end_seconds)} s`;
          return { id: at, speaker: null, beat: `Part ${(LETTERS[at] ?? "").toUpperCase()}${span}`, text: part.line };
        });
  const looks = [
    { field: "actor_description", label: "New actor" },
    { field: "product_description", label: "New pack" },
    { field: "source_product", label: "Source ad's pack" },
  ] as const;
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={asked !== null && !inputs.product}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
          <VideoSlot
            teamId={teamId}
            brandId={brandId}
            label="Source ad"
            icon={<Clapperboard size={20} strokeWidth={1.5} aria-hidden />}
            subtitle="The ad to remake, 4 to 30 seconds"
            tooltip={SOURCE_TIP}
            value={source}
            problem={problem}
            disabled={locked}
            onChange={(value) => {
              setPicked(value);
              const next = value !== null && "assetId" in value ? value.assetId : null;
              const sourceAd = next === null ? null : { kind: "asset" as const, asset_id: next };
              // Another source drops the script read from the old one, as the server's save does.
              if (next === sourceId) setInputs({ source_ad: sourceAd });
              else flow.edit({ inputs: { ...inputs, source_ad: sourceAd }, script: null });
            }}
          />
        </div>
        <Group gap={12} wrap="wrap">
          <InputChip
            label="Actor"
            value={inputs.actor_note}
            placeholder="A woman in her 30s, curly red hair (optional)"
            tooltip={ACTOR_TIP}
            maxLength={600}
            disabled={locked}
            onChange={(value) => setInputs({ actor_note: value })}
          />
          <Switch
            label={RIGHTS_LABEL}
            checked={inputs.rights}
            disabled={locked}
            onChange={(event) => setInputs({ rights: event.currentTarget.checked })}
          />
        </Group>
        <ScriptSection
          lines={rows}
          meta={rows === null ? null : `${rows.length} parts`}
          tooltip={SCRIPT_TIP}
          primary={null}
          maxWords={(index) => caps[index] ?? 0}
          hint={SCRIPT_HINT}
          writeLabel={WRITE_SCRIPT}
          writing={writing}
          locked={locked}
          issue={script !== null && !writing ? issue : null}
          warning={warnings.length === 0 ? null : warnings.join(" ")}
          onGenerate={() => {
            setAsked(draft.updated_at);
            if (missing.length === 0 && problem === null) flow.generateScript();
          }}
          onWrite={() => {
            // The directions come from the director: with no script, there is none to write in.
            if (script === null) setWriteAsked(true);
            else editScript(blankLines(script));
          }}
          onLine={(index, text) => {
            if (script === null) return;
            editScript({ parts: script.parts.map((part, at) => (at === index ? { ...part, line: text } : part)) });
          }}
        />
        {asked !== null && missing.length > 0 ? <Text className={classes.issue}>{missing.join(". ")}.</Text> : null}
        {writeAsked && script === null ? <Text className={classes.issue}>{GENERATE_FIRST}.</Text> : null}
        {script === null ? null : (
          <Stack gap={8} aria-label="The swap">
            {script.parts.map((part, at) =>
              part.beats.length === 0 ? null : (
                <Text key={at} size="xs" c="dimmed">
                  {`Part ${(LETTERS[at] ?? "").toUpperCase()}: ${part.beats.join(" · ")}`}
                </Text>
              ),
            )}
            {looks.map((look) => (
              <Textarea
                key={look.field}
                label={look.label}
                autosize
                minRows={1}
                maxLength={MAX_LOOK[look.field]}
                value={script[look.field]}
                disabled={locked}
                onChange={(event) => editScript({ [look.field]: event.currentTarget.value })}
              />
            ))}
          </Stack>
        )}
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
